import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './ApiContext';
import type {
    BindingUpsertRequest,
    CreateFeatureServiceRequest,
    ManualSyncRequest,
    PreflightRequest,
    ScheduleUpsertRequest,
    SyncBinding,
    SyncDirection,
    SyncSchedule,
} from './types';

/** Sentralt sted for query-nøkler — gjør invalidering forutsigbar. */
export const queryKeys = {
    health: ['health'] as const,
    metrics: ['metrics'] as const,
    bindings: (onlyEnabled: boolean) => ['bindings', { onlyEnabled }] as const,
    schedules: (bindingId: string) => ['schedules', bindingId] as const,
    datasets: (system: string) => ['datasets', system] as const,
    datasetSchema: (system: string, datasetId: string) => ['datasetSchema', system, datasetId] as const,
    datasetFeatures: (system: string, datasetId: string, cursor?: string) =>
        ['datasetFeatures', system, datasetId, cursor ?? null] as const,
};

export function useHealth(enabled = true) {
    const { client, hasToken } = useApi();
    return useQuery({
        queryKey: queryKeys.health,
        queryFn: () => client.getHealth(),
        enabled,
        refetchInterval: 15_000,
        // helse avhenger ikke av token, men vi vil ikke spamme før klient er klar
        meta: { hasToken },
    });
}

export function useMetrics(enabled = true) {
    const { client } = useApi();
    return useQuery({
        queryKey: queryKeys.metrics,
        queryFn: () => client.getMetrics(),
        enabled,
        refetchInterval: 10_000,
    });
}

export function useBindings(onlyEnabled = false) {
    const { client, hasToken } = useApi();
    return useQuery({
        queryKey: queryKeys.bindings(onlyEnabled),
        queryFn: () => client.listBindings(onlyEnabled),
        enabled: hasToken,
    });
}

export function useDatasets(system: string, enabled = true) {
    const { client, hasToken } = useApi();
    return useQuery({
        queryKey: queryKeys.datasets(system),
        queryFn: () => client.listDatasets(system),
        enabled: enabled && hasToken && !!system,
    });
}

export function useDatasetSchema(system: string, datasetId: string | undefined) {
    const { client, hasToken } = useApi();
    return useQuery({
        queryKey: queryKeys.datasetSchema(system, datasetId ?? ''),
        queryFn: () => client.getDatasetSchema(system, datasetId!),
        enabled: hasToken && !!system && !!datasetId,
    });
}

export function useCreateBinding() {
    const { client } = useApi();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: BindingUpsertRequest) => client.createBinding(body),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['bindings'] }),
    });
}

export function useUpdateBinding() {
    const { client } = useApi();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ bindingId, body }: { bindingId: string; body: BindingUpsertRequest }) =>
            client.updateBinding(bindingId, body),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['bindings'] }),
    });
}

export function useDeleteBinding() {
    const { client } = useApi();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (bindingId: string) => client.deleteBinding(bindingId),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['bindings'] }),
    });
}

/**
 * Kjør preflight mot en eksisterende AGOL-tjeneste. Mutation (ikke query) fordi
 * den inspiserer en ekstern tjeneste og trigges eksplisitt av brukeren.
 */
export function usePreflight() {
    const { client } = useApi();
    return useMutation({
        mutationFn: (body: PreflightRequest) => client.preflight(body),
    });
}

// --- Tidsstyrt synk ---

/** Tidsplaner (begge retninger) for én kobling. */
export function useSchedules(bindingId: string, enabled = true) {
    const { client, hasToken } = useApi();
    return useQuery({
        queryKey: queryKeys.schedules(bindingId),
        queryFn: () => client.listSchedules(bindingId),
        enabled: enabled && hasToken && !!bindingId,
        // Worker-en oppdaterer last_run/next_due serverside per tick — poll så
        // status-tidene i UI-et ikke blir stående på utdaterte verdier.
        refetchInterval: 5_000,
    });
}

/**
 * Tidsplaner for flere koblinger samtidig, for status-oversikten. API-et mangler
 * et bulk-endepunkt, så dette blir ett kall per kobling (N+1) kjørt parallelt.
 */
export function useSchedulesForBindings(bindings: SyncBinding[] | undefined) {
    const { client, hasToken } = useApi();
    const list = bindings ?? [];
    return useQueries({
        queries: list.map((b) => ({
            queryKey: queryKeys.schedules(b.bindingId),
            queryFn: () => client.listSchedules(b.bindingId),
            enabled: hasToken,
            refetchInterval: 5_000,
        })),
        combine: (results) => ({
            isLoading: results.some((r) => r.isLoading),
            isError: results.some((r) => r.isError),
            error: results.find((r) => r.isError)?.error ?? null,
            byBinding: new Map<string, SyncSchedule[]>(
                list.map((b, i) => [b.bindingId, results[i]?.data ?? []]),
            ),
            refetch: () => results.forEach((r) => r.refetch()),
        }),
    });
}

export function useUpsertSchedule() {
    const { client } = useApi();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({
            bindingId,
            direction,
            body,
        }: {
            bindingId: string;
            direction: SyncDirection;
            body: ScheduleUpsertRequest;
        }) => client.upsertSchedule(bindingId, direction, body),
        onSuccess: (_data, { bindingId }) =>
            qc.invalidateQueries({ queryKey: queryKeys.schedules(bindingId) }),
    });
}

export function useDeleteSchedule() {
    const { client } = useApi();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ bindingId, direction }: { bindingId: string; direction: SyncDirection }) =>
            client.deleteSchedule(bindingId, direction),
        onSuccess: (_data, { bindingId }) =>
            qc.invalidateQueries({ queryKey: queryKeys.schedules(bindingId) }),
    });
}

export function useRequestSync() {
    const { client } = useApi();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: ManualSyncRequest) => client.requestSync(body),
        onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.metrics }),
    });
}

export function useStartInitialLoad() {
    const { client } = useApi();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (bindingId: string) => client.startInitialLoad(bindingId),
        onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.metrics }),
    });
}

export function useCreateFeatureService() {
    const { client } = useApi();
    return useMutation({
        mutationFn: (body: CreateFeatureServiceRequest) => client.createFeatureService(body),
    });
}
